package com.evoapps.evoshift.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.saveable.LocalSaveableStateRegistry
import androidx.compose.runtime.saveable.SaveableStateRegistry
import com.evoapps.evoshift.domain.model.Appearance
import com.evoapps.evoshift.ui.AppAppearance
import com.evoapps.evoshift.util.KeepPlatformLanguage
import org.kodein.di.compose.rememberInstance

/**
 * Shows [content] in the language picked in the app (SH-137) and switches it at once when another
 * one is picked, on every platform.
 *
 * Compose Multiplatform resources read the platform's locale ([com.evoapps.evoshift.util.AppLocale]
 * points it at the picked language) only when a text is composed again, so a pick composes the
 * whole app again. What the screens keep with `rememberSaveable` (the navigation back
 * stack, open tabs, typed text) is handed over to the new composition, so the person stays on the
 * same screen; view models live outside the composition and stay as they are.
 *
 * Also keeps the profile's language in step ([ProfileLanguageSync]), outside the part that is
 * composed again. SH-196: a new pick of the app's colours (AppAppearance) is switched the same way,
 * and [ProfileAppearanceSync] follows the profile's pick.
 */
@Composable
fun ProvideAppLanguage(content: @Composable () -> Unit) {
    val settings: AppLanguageSettings by rememberInstance()
    val picked by settings.preference.collectAsState()
    KeepPlatformLanguage(picked)

    val profileSync: ProfileLanguageSync by rememberInstance()
    LaunchedEffect(profileSync) { profileSync.start(this) }
    // SH-196: the colours the same way: a new pick composes the content again, so colours a
    // composable kept (remember) are taken again too; the profile's pick is followed.
    val appearanceSync: ProfileAppearanceSync by rememberInstance()
    LaunchedEffect(appearanceSync) { appearanceSync.start(this) }
    val shown = Shown(picked, AppAppearance.current)

    val parent = LocalSaveableStateRegistry.current
    val holder = remember { LanguageStateHolder(parent) }
    DisposableEffect(parent, holder) {
        val entry = parent?.registerProvider(STATE_KEY) { holder.save() }
        onDispose { entry?.unregister() }
    }
    // The language the content is composed in. A new pick leaves the content out for one frame
    // (after saving its state) and composes it again at the same place, so every
    // rememberSaveable finds its value under the same key.
    var composedIn by remember { mutableStateOf(shown) }
    if (shown != composedIn) {
        holder.saveForSwitch()
        SideEffect { composedIn = shown }
    } else {
        holder.composed()
        CompositionLocalProvider(LocalSaveableStateRegistry provides holder.registry) { content() }
    }
}

private const val STATE_KEY = "evoshift.languageState"

/** What the content is composed in: the picked language and the colours (SH-196). */
private data class Shown(val language: String?, val appearance: Appearance)

/**
 * SH-196: applies the colours remembered on this device before the first frame (ThemeSettings is
 * created here, if it was not yet). Call it inside the DI, before [com.evoapps.evoshift.ui.AppTheme].
 */
@Composable
fun ApplyRememberedAppearance() {
    val settings: ThemeSettings by rememberInstance()
    remember(settings) { settings.current }
}

/**
 * The saveable state of the app's content across language switches: before the content leaves,
 * its state is saved and the next composition starts from it. It is also part of the platform's
 * saved state (process death, Android configuration changes).
 */
private class LanguageStateHolder(private val parent: SaveableStateRegistry?) {
    var registry: SaveableStateRegistry = newRegistry(parent?.consumeRestored(STATE_KEY).asSavedMap())
        private set
    private var saving = false

    private fun newRegistry(restored: Map<String, List<Any?>>?) =
        SaveableStateRegistry(restored) { parent?.canBeSaved(it) ?: true }

    /** Called while composing, before the old content leaves: its values are still there. */
    fun saveForSwitch() {
        if (saving) return
        saving = true
        registry = newRegistry(registry.performSave())
    }

    /** The content is composed again: the next switch saves again. */
    fun composed() {
        saving = false
    }

    fun save(): Map<String, List<Any?>> = registry.performSave()

    @Suppress("UNCHECKED_CAST")
    private fun Any?.asSavedMap(): Map<String, List<Any?>>? = this as? Map<String, List<Any?>>
}
